import React, { useEffect, useMemo, useState } from 'react';
import {
    Grid,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Toolbar,
    Alert,
    Box,
    IconButton,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import toast from 'react-hot-toast';
import {
    ButtonWithIcon,
    DeleteModal,
    Input,
    SearchableDropdown,
    Select,
    SecondaryButton,
    SegmentedButtonGroup,
    WeightInput,
} from '../../Common';
import Enums from '../../../enums';
import { cylinderOptionLabel, getContainerSpacing, getConvertedWeight, getTotalGasAmount, yes_no_options } from '../utils';
import { TranslateW, Translate } from '../../../Utils/Languages/Translate';
import { trakrefFontFamily } from '../../../utils';
import { Grey3, lightGrayColor, white } from '../../../color_utils';
import { renderWeight } from '../../../Utils/weight_utils';

const TRANSFER_TYPE = { RECOVER: 'recover', INSTALL: 'install' };
const REQUIRED_FIELDS_KEY = 'GasTransaction';

// When true, the Transfer Amount field renders as a single decimal-lbs input (matching the
// Figma "Transfer Amount (lbs)" design) instead of the dual lbs + oz fields. Flip to false to
// restore the dual-field behavior for Lbs-preference users.
const SINGLE_LBS_TRANSFER_AMOUNT = true;

// When true, the Transfer Amount uses a floating field label (like the Cylinder / Status fields)
// instead of a separate "Transfer Amount*" title above the input. Flip to false to show the title.
const TRANSFER_AMOUNT_HEADER_AS_LABEL = true;

const recoveryColumns = ['CYLINDER', 'AMOUNT', 'GAS STATUS', 'NOTES', 'ACTIONS'];
const installColumns = ['CYLINDER', 'AMOUNT', 'PURPOSE', 'NOTES', 'ACTIONS'];

const headerCellStyle = {
    color: Grey3,
    fontSize: '10px',
    fontFamily: trakrefFontFamily,
    textTransform: 'uppercase',
};

const bodyCellStyle = { fontFamily: trakrefFontFamily };

const renderNotes = (cylinderNotes, transferNotes) => {
    const parts = [cylinderNotes, transferNotes].filter(Boolean);
    return parts.join(' — ');
};

// A row's stable identifier for edit/delete matching, in priority order:
// transferID (persisted) → localCylinderId (in-session unknown cylinder) → index.
const rowIdentifier = (row, index) => {
    if (row.transferID != null) return { type: 'transferID', value: row.transferID };
    if (row.localCylinderId) return { type: 'localCylinderId', value: row.localCylinderId };
    return { type: 'index', value: index };
};

const matchRow = (row, idx, target) => {
    if (target.type === 'transferID') return row.transferID === target.value;
    if (target.type === 'localCylinderId') return row.localCylinderId === target.value;
    return idx === target.value;
};

const TransferTableSection = ({
    title,
    count,
    columns,
    rows,
    emptyText,
    mode,
    total,
    totalLabel,
    onEdit,
    onDelete,
}) => (
    <Grid item xs={12}>
        <TableContainer component={Paper}>
            <Toolbar>
                <Typography
                    sx={{ flex: '1 1 100%', fontFamily: trakrefFontFamily }}
                    variant="subtitle2"
                >
                    {title}
                    {count > 0 && (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '20px',
                                height: '20px',
                                marginLeft: '8px',
                                padding: '0 6px',
                                borderRadius: '10px',
                                backgroundColor: 'primary.main',
                                color: "white",
                                fontSize: '11px',
                                fontFamily: trakrefFontFamily,
                            }}
                        >
                            {count}
                        </Box>
                    )}
                </Typography>
            </Toolbar>
            <Table aria-label={`${title} table`}>
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableCell key={col} style={headerCellStyle}>
                                {col}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                align="center"
                                style={{ ...bodyCellStyle, color: Grey3, padding: '24px' }}
                            >
                                {emptyText}
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, index) => {
                            const ident = rowIdentifier(row, index);
                            return (
                                <TableRow
                                    key={`${ident.type}-${ident.value}`}
                                    sx={{ backgroundColor: index % 2 ? white : lightGrayColor }}
                                >
                                    <TableCell style={bodyCellStyle}>{row.name}</TableCell>
                                    <TableCell style={bodyCellStyle}>
                                        {row.amount ? renderWeight(row.amount) : ''}
                                    </TableCell>
                                    <TableCell style={bodyCellStyle}>
                                        {mode === 'install' ? row.purposeName : row.recoverGasStatusName}
                                    </TableCell>
                                    <TableCell style={bodyCellStyle}>
                                        {renderNotes(row.cylinderNotes, row.notes)}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => onEdit(ident)} size="small">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton onClick={() => onDelete(ident, row.name)} size="small">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
            {rows.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: lightGrayColor,
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: trakrefFontFamily,
                            color: Grey3,
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                        }}
                    >
                        {totalLabel}
                    </Typography>
                    <Typography sx={{ fontFamily: trakrefFontFamily, fontWeight: 600 }}>
                        {renderWeight(total)}
                    </Typography>
                </Box>
            )}
        </TableContainer>
    </Grid>
);

// Net summary strip — running totals across all saved transfers (per AC9 + Figma).
const NetSummaryStrip = ({ totalRecovered, totalInstalled, showRecovered, showInstalled }) => (
    <Grid item xs={12}>
        <Paper sx={{ display: 'flex', overflow: 'hidden' }}>
            {showRecovered && (
                <Box sx={{ flex: 1, padding: '10px 16px' }}>
                    <Typography
                        sx={{
                            fontFamily: trakrefFontFamily,
                            color: Grey3,
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                        }}
                    >
                        Total Recovered
                    </Typography>
                    <Typography sx={{ fontFamily: trakrefFontFamily, fontWeight: 600 }}>
                        {renderWeight(totalRecovered)}
                    </Typography>
                </Box>
            )}
            {showRecovered && showInstalled && (
                <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: lightGrayColor }} />
            )}
            {showInstalled && (
                <Box sx={{ flex: 1, padding: '10px 16px' }}>
                    <Typography
                        sx={{
                            fontFamily: trakrefFontFamily,
                            color: Grey3,
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                        }}
                    >
                        Total Installed
                    </Typography>
                    <Typography sx={{ fontFamily: trakrefFontFamily, fontWeight: 600 }}>
                        {renderWeight(totalInstalled)}
                    </Typography>
                </Box>
            )}
        </Paper>
    </Grid>
);

const emptyEntryForm = {
    selectedCylinder: { id: null, name: '' },
    amount: null,
    cylinderNotes: '',
    transferNotes: '',
    recoverGasStatus: { id: null, name: '' },
    materialStatus: { id: null, name: '' },
};

const hydrateEntryFormFromRow = (row) => ({
    selectedCylinder: {
        id: row.cylinderId,
        name: row.name,
        currentGasWeightLbs: row.currentGasWeightLbs,
        materialTypeId: row.materialTypeID,
        maxGasCapacityLbs: row.maxGasCapacityLbs,
    },
    amount: row.amount,
    cylinderNotes: row.cylinderNotes || '',
    transferNotes: row.notes || '',
    recoverGasStatus: {
        id: row.recoverGasStatusId ?? null,
        name: row.recoverGasStatusName ?? '',
    },
    materialStatus: {
        id: row.materialStatusID ?? null,
        name: row.materialStatusName ?? '',
    },
});

// Soft-delete: persisted rows stay in the array with enabled:false (backend
// handles the soft-delete on save). In-session rows (no transferID) are
// dropped entirely.
const applySoftDelete = (arr, target) => arr
    .map((row, idx) => (matchRow(row, idx, target) ? { ...row, enabled: false } : row))
    .filter(row => row.transferID != null || row.enabled);


const GasTransaction = ({
    formState,
    formState: {
        workItemTypeId: serviceEventType,
        recoveries = [],
        installs = [],
        serviceTransferReasonId,
        newGasChargeCapacityLbs,
        serviceOrLeakRepairError = false,
        assetDetails: {
            maxGasCapacity: assetMaxGasCapacity,
            currentGasWeightLbs: assetCurrentGasWeight = 0,
        } = {},
    },
    setGlobalState,
    dropdownOptions,
    requiredFieldsMissing = {},
    isRetroFit,
    setRequiredFields,
    requiredFieldsRef,
    gasTransactionDirtyRef,
}) => {
    const isInstallSE = serviceEventType === Enums.SE.WorkItemType.Install;
    const isReinstallSE = serviceEventType === Enums.SE.WorkItemType.Reinstall;
    const isShutdownMothballSE = serviceEventType === Enums.SE.WorkItemType.ShutdownMothball;
    const isRecordRefrigerantSE = serviceEventType === Enums.SE.WorkItemType.RecordRefrigerant;

    const isInstallOnlySE = isInstallSE || isReinstallSE;
    const isRecoverOnlySE = isShutdownMothballSE;
    const showRecoveryTable = !isInstallOnlySE;
    const showInstallTable = !isRecoverOnlySE;
    const purposeApplies = !(isInstallSE || isReinstallSE);

    const defaultTransferType = isInstallOnlySE
        ? TRANSFER_TYPE.INSTALL
        : isRecoverOnlySE
            ? TRANSFER_TYPE.RECOVER
            : null;

    const [entryFormOpen, setEntryFormOpen] = useState(false);
    const [transferType, setTransferType] = useState(defaultTransferType);
    const [entryForm, setEntryForm] = useState(emptyEntryForm);
    const [editingTarget, setEditingTarget] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    // WeightInput reads `value` only on mount, so bump this key to remount it whenever we
    // set the amount programmatically (reset after save, hydrate on edit, retrofit pre-fill).
    const [amountFieldKey, setAmountFieldKey] = useState(0);
    const remountAmountField = () => setAmountFieldKey(prev => prev + 1);
    // Toast text computed here (TranslateW is a hook — can't run in the save handlers).
    const toastText = {
        recoveryAdded: TranslateW('gasTransaction.recovery-added') || 'The recovery transfer has been successfully added.',
        recoveryUpdated: TranslateW('gasTransaction.recovery-updated') || 'The recovery transfer has been successfully updated.',
        installAdded: TranslateW('gasTransaction.install-added') || 'The install transfer has been successfully added.',
        installUpdated: TranslateW('gasTransaction.install-updated') || 'The install transfer has been successfully updated.',
    };

    const isEditing = editingTarget != null;
    const isRecover = transferType === TRANSFER_TYPE.RECOVER;
    const isInstall = transferType === TRANSFER_TYPE.INSTALL;
    const isUnknownCylinder = entryForm.selectedCylinder.id === 0;

    // AC10 — selecting a cylinder already used in another saved transfer of the same type is a
    // soft warning (the same physical cylinder can be split across entries), NOT a hard block.
    // The Unknown cylinder (id 0) is exempt since each Unknown entry is tracked separately.
    const duplicateCylinderSelected = (() => {
        const id = entryForm.selectedCylinder.id;
        if (!id || id === 0) return false;
        const arr = isRecover ? recoveries : installs;
        return arr.some((r, idx) => (
            r.enabled
            && r.cylinderId === id
            && !(isEditing && matchRow(r, idx, editingTarget.target))
        ));
    })();

    const purposeName = (dropdownOptions.transferReasons || [])
        .find(r => r.id === serviceTransferReasonId)?.name || '';

    const recoveryRows = recoveries.filter(r => r.enabled).map(r => ({ ...r }));
    const installRows = installs
        .filter(r => r.enabled)
        .map(r => ({ ...r, purposeName }));

    // AC15 (loosened, #15492): warn when "Was Gas Transferred?" = Yes but no transfer is saved —
    // ONLY when the toggle is reachable, i.e. a leak/circuit was found at Discovery so the
    // Verification step that hosts the toggle is rendered. When Leak Found = No the toggle isn't
    // rendered (defaults Yes), so the warning must not appear.
    const showZeroTransferWarning = (
        serviceEventType === Enums.SE.WorkItemType.ServiceOrLeakRepair
        && formState.initialLeakInspection?.wasLeakFound === true
        && (formState.wasGasTransferred ?? true) === true
        && recoveryRows.length === 0
        && installRows.length === 0
    );

    const runningRecoverTotal = useMemo(
        () => getTotalGasAmount(recoveries),
        [recoveries],
    );
    const runningInstallTotal = useMemo(
        () => getTotalGasAmount(installs),
        [installs],
    );
    const convertedAssetMaxGasCapacity = getConvertedWeight(assetMaxGasCapacity);


    // Only enforce new-system capacity once it's a valid positive number;
    // an unset value otherwise blocks installs and prints "undefined lbs.".
    const newSystemMaxCapacity = Number(newGasChargeCapacityLbs);
    const newSystemCapacityKnown = Number.isFinite(newSystemMaxCapacity) && newSystemMaxCapacity > 0;

    // Recover validation (per §15.4 / GasModal.js:83–93)
    const recoverCylinderOverflow = (
        isRecover
        && !isUnknownCylinder
        && entryForm.amount > 0
        && (entryForm.amount + (entryForm.selectedCylinder.currentGasWeightLbs || 0))
            > (entryForm.selectedCylinder.maxGasCapacityLbs || 0)
    );
    // Applies to Unknown Cylinder too — the limit is the asset's, not the receiving cylinder's (Bug 16194).
    const recoverAssetOverflow = (
        isRecover
        && (entryForm.amount + runningRecoverTotal) > convertedAssetMaxGasCapacity
    );

    // Install validation (per §16.4 / GasModal.js:94–104)
    const installAssetOverflow = (
        isInstall
        && !isRetroFit
        && (entryForm.amount + runningInstallTotal) > convertedAssetMaxGasCapacity
    );
    const installNewAssetOverflow = (
        isInstall
        && isRetroFit
        && newSystemCapacityKnown
        && entryForm.amount > newSystemMaxCapacity
        && entryForm.amount > newGasChargeCapacityLbs
    );
    const installCylinderOverflow = (
        isInstall
        && !isUnknownCylinder
        && !!entryForm.selectedCylinder.id
        && entryForm.amount > 0
        && (
            entryForm.amount > (entryForm.selectedCylinder.currentGasWeightLbs || 0)
            || installAssetOverflow
            || installNewAssetOverflow
        )
    );

    const commonGasErrors = (
        entryForm.selectedCylinder.id === undefined
        || entryForm.selectedCylinder.id === null
        || entryForm.amount === null
        || entryForm.amount === 0
        || isNaN(entryForm.amount)
    );

    // Shutdown\Mothball recovery: block save until "Vacuum Pulled?" is answered (No is valid; only unanswered blocks).
    const isVacuumPulledMissing = (
        isRecover && isShutdownMothballSE
        && formState.wasProperVacuumPulled == null
    );

    // AC3 — "Status of Recovered Gas" is a required field on a recovery transfer.
    const recoverStatusMissing = isRecover && entryForm.recoverGasStatus.id == null;

    // AC4 — "Purpose for Adding Gas" is required on an install transfer (where it applies).
    const purposeMissing = isInstall && purposeApplies && serviceTransferReasonId == null;

    const disableSave = (
        commonGasErrors
        || isVacuumPulledMissing
        || (isRecover && (recoverStatusMissing || recoverCylinderOverflow || recoverAssetOverflow))
        || (isInstall && (purposeMissing || installCylinderOverflow || installAssetOverflow || installNewAssetOverflow))
    );

    // Step-level Install warnings (per InstallGas.js:113–131)
    const determineServiceOrLeakRepairError = (transferReasonId) => (
        runningInstallTotal + assetCurrentGasWeight > assetMaxGasCapacity
        && serviceEventType === Enums.SE.WorkItemType.ServiceOrLeakRepair
        && transferReasonId === Enums.ServiceTransferReasonTypes.InitialCharge
    );
    const retrofitTotalsError = (
        serviceEventType === Enums.SE.WorkItemType.Retrofit
        && newSystemCapacityKnown
        && runningInstallTotal > newSystemMaxCapacity
    );
    const purposeIsInitialCharge = (
        serviceTransferReasonId === Enums.ServiceTransferReasonTypes.InitialCharge
    );
    // The old asset's remaining headroom doesn't apply to a Retrofit — its gas is being
    // replaced and the ceiling is the NEW system capacity (retrofitTotalsError above).
    // Without the exclusion, an initial charge into a full old system always errored,
    // displayed with the retrofit message's unrelated numbers (#15516).
    const initialChargeTotalsError = (
        !isRetroFit
        && runningInstallTotal > (assetMaxGasCapacity - assetCurrentGasWeight)
        && purposeIsInitialCharge
    );

    // A saved transfer can reference a cylinder that's no longer in the active list (e.g.
    // decommissioned since the SE was created) — append the hydrated selection so the
    // Autocomplete still displays it in edit mode.
    const cylinderList = (isRecover
        ? dropdownOptions.recoveryCylinders
        : dropdownOptions.installCylinders) || [];
    const cylinderOptions = useMemo(() => {
        const selected = entryForm.selectedCylinder;
        if (selected.id != null && !cylinderList.some(c => c.id === selected.id)) {
            return [...cylinderList, selected];
        }
        return cylinderList;
    }, [cylinderList, entryForm.selectedCylinder]);

    const handleCylinderSelect = (cylinderId) => {
        const selected = cylinderOptions.find(c => c.id === cylinderId) || { id: null, name: '' };
        // Status of Recovered Gas / Material Status are now standard, always-visible fields
        // (per Figma "New Transfer" container + AC3/AC4), so they persist across cylinder changes.
        setEntryForm(prev => ({
            ...prev,
            selectedCylinder: selected,
        }));
    };

    const handleRecoverGasStatusSelect = (id) => {
        const selected = (dropdownOptions.recoverGasStatuses || [])
            .find(s => s.id === id) || { id: null, name: '' };
        setEntryForm(prev => ({ ...prev, recoverGasStatus: selected }));
    };

    const handleMaterialStatusSelect = (id) => {
        const selected = (dropdownOptions.materialStatuses || [])
            .find(s => s.id === id) || { id: null, name: '' };
        setEntryForm(prev => ({ ...prev, materialStatus: selected }));
    };

    // Per InstallGas.js:79–90 — key renamed from "InstallGas" to "GasTransaction"
    const handleNewRequiredFields = (installCount) => {
        if (!requiredFieldsRef || !setRequiredFields) return;
        let nextRequired = { ...requiredFieldsRef.current };
        if (installCount > 0 && purposeApplies) {
            nextRequired = {
                ...nextRequired,
                [REQUIRED_FIELDS_KEY]: ['serviceTransferReasonId'],
            };
        } else if (nextRequired[REQUIRED_FIELDS_KEY]?.includes('serviceTransferReasonId')) {
            const { [REQUIRED_FIELDS_KEY]: _, ...rest } = nextRequired;
            nextRequired = rest;
        }
        requiredFieldsRef.current = nextRequired;
        setRequiredFields(nextRequired);
    };

    // Per InstallGas.js:150–157
    const handlePurposeForAddingGas = (transferReasonId) => {
        delete formState.serviceOrLeakRepairError;
        setGlobalState('serviceTransferReasonId', transferReasonId);
        if (determineServiceOrLeakRepairError(transferReasonId)) {
            setGlobalState('serviceOrLeakRepairError', true);
        }
    };

    // SE 13 — Purpose is locked to Leak Repair for Record Refrigerant (US 11747; the select
    // below is disabled). InstallGas.js:159–163 pinned the raw id 1 = LeakRepair; the port
    // mistranslated it to InitialCharge, which also tripped the max-capacity error (#15493).
    useEffect(() => {
        if (isRecordRefrigerantSE) {
            handlePurposeForAddingGas(Enums.ServiceTransferReasonTypes.LeakRepair);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Retrofit pre-fill amount when entering install mode with an empty form (per §16.6)
    useEffect(() => {
        if (entryFormOpen && isInstall && isRetroFit && !isEditing && entryForm.amount === null) {
            setEntryForm(prev => ({ ...prev, amount: newGasChargeCapacityLbs }));
            remountAmountField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryFormOpen, transferType]);

    // Flag (via ref) when the open entry form holds real unsaved input, so the wizard can warn
    // before navigating away. amount > 0, not != null: WeightInput emits 0 on mount.
    const hasUnsavedEntry = entryFormOpen && (
        entryForm.selectedCylinder.id !== null
        || Number(entryForm.amount) > 0
        || !!entryForm.cylinderNotes
        || !!entryForm.transferNotes
    );
    if (gasTransactionDirtyRef) gasTransactionDirtyRef.current = hasUnsavedEntry;
    // Clear on unmount so the flag never lingers after leaving the step.
    useEffect(() => () => {
        if (gasTransactionDirtyRef) gasTransactionDirtyRef.current = false;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const resetEntryForm = () => {
        setEntryForm(emptyEntryForm);
        remountAmountField();
    };

    const closeForm = () => {
        resetEntryForm();
        setEntryFormOpen(false);
        setEditingTarget(null);
    };

    const handleAddTransferClick = () => {
        if (!transferType) {
            setTransferType(TRANSFER_TYPE.RECOVER);
        }
        setEditingTarget(null);
        setEntryFormOpen(true);
    };

    const handleCancel = () => closeForm();

    const handleTransferTypeChange = (newType) => {
        if (isEditing) return;
        setTransferType(newType);
        resetEntryForm();
    };

    const handleEdit = (mode) => (target) => {
        const arr = mode === TRANSFER_TYPE.RECOVER ? recoveries : installs;
        const row = arr.find((r, idx) => matchRow(r, idx, target));
        if (!row) return;
        setTransferType(mode);
        setEntryForm(hydrateEntryFormFromRow(row));
        remountAmountField();
        setEditingTarget({ mode, target });
        setEntryFormOpen(true);
    };

    const handleDeleteRequest = (mode) => (target, name) => {
        setPendingDelete({ mode, target, name });
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        const { mode, target } = pendingDelete;
        if (mode === TRANSFER_TYPE.RECOVER) {
            const next = applySoftDelete(recoveries, target);
            setGlobalState('recoveries', next);
        } else {
            const next = applySoftDelete(installs, target);
            setGlobalState('installs', next);
            handleNewRequiredFields(next.filter(r => r.enabled).length);
        }
        setPendingDelete(null);
    };

    const buildRecoverRow = () => {
        const { selectedCylinder, amount, cylinderNotes, transferNotes, recoverGasStatus } = entryForm;
        const row = {
            cylinderId: selectedCylinder.id,
            name: selectedCylinder.name,
            amount,
            cylinderNotes,
            notes: transferNotes,
            currentGasWeightLbs: selectedCylinder.currentGasWeightLbs,
            materialTypeID: selectedCylinder.materialTypeId,
            maxGasCapacityLbs: selectedCylinder.maxGasCapacityLbs,
            enabled: true,
            recoverGasStatusId: recoverGasStatus.id,
            recoverGasStatusName: recoverGasStatus.name,
        };
        if (selectedCylinder.id === 0) {
            row.localCylinderId = Math.random().toString();
        }
        return row;
    };

    const buildInstallRow = () => {
        const { selectedCylinder, amount, cylinderNotes, transferNotes, materialStatus } = entryForm;
        const row = {
            cylinderId: selectedCylinder.id,
            name: selectedCylinder.name,
            amount,
            cylinderNotes,
            notes: transferNotes,
            currentGasWeightLbs: selectedCylinder.currentGasWeightLbs,
            materialTypeID: selectedCylinder.materialTypeId,
            maxGasCapacityLbs: selectedCylinder.maxGasCapacityLbs,
            enabled: true,
            materialStatusID: materialStatus.id,
            materialStatusName: materialStatus.name,
        };
        if (selectedCylinder.id === 0) {
            row.localCylinderId = Math.random().toString();
        }
        return row;
    };

    const handleSaveRecover = () => {
        if (isEditing) {
            const updated = recoveries.map((row, idx) => (
                matchRow(row, idx, editingTarget.target)
                    ? { ...row, ...buildRecoverRow(), localCylinderId: row.localCylinderId, transferID: row.transferID }
                    : row
            ));
            setGlobalState('recoveries', updated);
            toast.success(toastText.recoveryUpdated, { duration: 4000 });
            closeForm();
            return;
        }
        const row = buildRecoverRow();
        setGlobalState('recoveries', [...recoveries, row]);
        toast.success(toastText.recoveryAdded, { duration: 4000 });
        // Close the entry container after a successful add so only the tables remain;
        // "Add Transfer" re-opens it for the next entry.
        closeForm();
    };

    const handleSaveInstall = () => {
        // Hard guard (AC4): never add/update an install transfer without a Purpose for Adding Gas
        // where it applies — backstops the disabled Save button.
        if (purposeMissing) return;
        if (isEditing) {
            const updated = installs.map((row, idx) => (
                matchRow(row, idx, editingTarget.target)
                    ? { ...row, ...buildInstallRow(), localCylinderId: row.localCylinderId, transferID: row.transferID }
                    : row
            ));
            setGlobalState('installs', updated);
            handleNewRequiredFields(updated.filter(r => r.enabled).length);
            if (purposeApplies && serviceTransferReasonId != null) {
                handlePurposeForAddingGas(serviceTransferReasonId);
            }
            toast.success(toastText.installUpdated, { duration: 4000 });
            closeForm();
            return;
        }
        const row = buildInstallRow();
        const nextInstalls = [...installs, row];
        setGlobalState('installs', nextInstalls);
        handleNewRequiredFields(nextInstalls.length);
        if (purposeApplies && serviceTransferReasonId != null) {
            handlePurposeForAddingGas(serviceTransferReasonId);
        }
        toast.success(toastText.installAdded, { duration: 4000 });
        // Close the entry container after a successful add so only the tables remain;
        // "Add Transfer" re-opens it for the next entry.
        closeForm();
    };

    const handleSave = () => (isRecover ? handleSaveRecover() : handleSaveInstall());

    const transferTypeOptions = [
        {
            value: TRANSFER_TYPE.RECOVER,
            label: TranslateW('recoverGasView.recover-gas') || 'Recover Gas',
            disabled: isInstallOnlySE || (isEditing && editingTarget.mode !== TRANSFER_TYPE.RECOVER),
        },
        {
            value: TRANSFER_TYPE.INSTALL,
            label: TranslateW('installGas.install-gas') || 'Install Gas',
            disabled: isRecoverOnlySE || (isEditing && editingTarget.mode !== TRANSFER_TYPE.INSTALL),
        },
    ];

    const showStepLevelInstallWarning = (
        showInstallTable
        && (serviceOrLeakRepairError || retrofitTotalsError || initialChargeTotalsError)
    );

    const stepLevelInstallWarningText = (() => {
        // Only show the new-system message when that check actually failed, so
        // we never print "undefined" or mislabel an asset-capacity overflow.
        if (isRetroFit && retrofitTotalsError) {
            return `Total of gas installs (${runningInstallTotal} lbs.) `
                + `must not exceed the new system max gas capacity `
                + `(${newGasChargeCapacityLbs} lbs.)`;
        }
        let text = `Total of gas installs plus current weight `
            + `(${runningInstallTotal + assetCurrentGasWeight} lbs.) must not exceed the system max gas capacity `
            + `(${assetMaxGasCapacity} lbs.)`;
        if (initialChargeTotalsError) text += ' during an initial charge.';
        return text;
    })();

    const showStepLevelRecoverWarning = (
        showRecoveryTable
        && !entryFormOpen
        && runningRecoverTotal > convertedAssetMaxGasCapacity
    );

    const deleteModeLabel = pendingDelete?.mode === TRANSFER_TYPE.RECOVER ? 'Recovery' : 'Install';

    return (
        <Grid container spacing={getContainerSpacing()}>
            <Grid item xs={12}>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <Typography variant="h6" sx={{ fontFamily: trakrefFontFamily }}>
                            Gas Transaction
                        </Typography>
                        <Typography variant="body2" sx={{ color: Grey3, fontFamily: trakrefFontFamily }}>
                            Record all refrigerant recovered and installed during this
                            service event. Multiple cylinders per type are supported.
                        </Typography>
                    </Grid>
                    <Grid item>
                        <ButtonWithIcon
                            buttonText="Add Transfer"
                            Icon={AddCircleIcon}
                            iconPosition="start"
                            onClick={handleAddTransferClick}
                            disabled={entryFormOpen}
                        />
                    </Grid>
                </Grid>
            </Grid>

            {showZeroTransferWarning && (
                <Grid item xs={12}>
                    <Alert severity="warning">
                        {TranslateW('gasTransaction.zero-transfer-warning')
                            || 'You answered Yes to Was Gas Transferred?, but no transfers have been saved. '
                            + 'Please add at least 1 transfer, or return to Step 3 and update your response to '
                            + 'Was Gas Transferred?'}
                    </Alert>
                </Grid>
            )}

            {/* Shutdown/Mothball requires the vacuum question regardless of whether a
                recovery transfer is recorded. Render it at the step level (not inside the
                optional Add-Transfer sub-form) so the step is always completable. Writes to
                the same formState.wasProperVacuumPulled that gates Save Transfer + COMPLETE. */}
            {isShutdownMothballSE && (
                <Grid item xs={12}>
                    <Paper sx={{ padding: '16px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <SegmentedButtonGroup
                                    label={TranslateW('recoverGas.was-appropriate-vacuum-pulled')}
                                    required
                                    value={formState.wasProperVacuumPulled}
                                    onChange={value => setGlobalState('wasProperVacuumPulled', value)}
                                    error={requiredFieldsMissing.wasProperVacuumPulled}
                                    options={(yes_no_options || []).map(o => ({ value: o.id, label: o.name }))}
                                />
                            </Grid>
                            {formState.wasProperVacuumPulled === true && (
                                <Grid item xs={12} md={6}>
                                    <Input
                                        fullWidth
                                        type="number"
                                        label={TranslateW('VacuumInHG')}
                                        value={formState.vacuumPsi}
                                        onChange={e => setGlobalState('vacuumPsi', e.target.value)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>
            )}

            {entryFormOpen && (
                <Grid item xs={12}>
                    <Paper sx={{ padding: '16px' }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontFamily: trakrefFontFamily, marginBottom: '12px' }}
                        >
                            {isEditing ? 'EDIT TRANSFER' : 'NEW TRANSFER'}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <SegmentedButtonGroup
                                    label="Transfer Type"
                                    required
                                    value={transferType}
                                    onChange={handleTransferTypeChange}
                                    options={transferTypeOptions}
                                    disabled={isEditing}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <SearchableDropdown
                                    required
                                    options={cylinderOptions}
                                    label={TranslateW('installGas.cylinder')}
                                    value={entryForm.selectedCylinder.id}
                                    onChange={handleCylinderSelect}
                                    searchFields={['name', 'label']}
                                    getOptionLabel={cylinderOptionLabel}
                                    emptyStateText="No matching cylinder found"
                                />
                            </Grid>
                            {duplicateCylinderSelected && (
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        {TranslateW('gasTransaction.duplicate-cylinder-warning')
                                            || 'This cylinder is already used in another saved transfer. You can still '
                                            + 'continue if the same physical cylinder is being split across entries.'}
                                    </Alert>
                                </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                                <WeightInput
                                    key={amountFieldKey}
                                    required
                                    removePadding
                                    showConversionArrows={!SINGLE_LBS_TRANSFER_AMOUNT}
                                    singleLbsField={SINGLE_LBS_TRANSFER_AMOUNT}
                                    headerAsLabel={TRANSFER_AMOUNT_HEADER_AS_LABEL}
                                    header={TranslateW('installGas.transfer-amount')}
                                    value={entryForm.amount}
                                    onChange={amount => setEntryForm(prev => ({ ...prev, amount }))}
                                    disabled={entryForm.selectedCylinder.id === null}
                                    columnSpan={12}
                                />
                            </Grid>

                            {isRecover && (
                                <Grid item xs={12} md={6}>
                                    <Select
                                        required
                                        options={dropdownOptions.recoverGasStatuses}
                                        label={TranslateW('rapidCylinderEntry.cylinderInputForm.recover-gas-status')}
                                        value={entryForm.recoverGasStatus.id}
                                        onChange={e => handleRecoverGasStatusSelect(e.target.value)}
                                        disabled={entryForm.selectedCylinder.id === null}
                                    />
                                </Grid>
                            )}
                            {isRecover && (
                                <Grid item xs={12} md={6}>
                                    <Select
                                        options={dropdownOptions.recoveryEquipment}
                                        label={TranslateW('RecoveryEquipmentUsed')}
                                        value={formState.recoveryEquipmentId}
                                        onChange={e => setGlobalState('recoveryEquipmentId', e.target.value)}
                                        disabled={entryForm.selectedCylinder.id === null}
                                    />
                                </Grid>
                            )}
                            {isInstall && (
                                <Grid item xs={12} md={6}>
                                    <Select
                                        options={dropdownOptions.materialStatuses}
                                        label={TranslateW('rapidCylinderEntry.cylinderInputForm.material-status')}
                                        value={entryForm.materialStatus.id}
                                        onChange={e => handleMaterialStatusSelect(e.target.value)}
                                        disabled={entryForm.selectedCylinder.id === null}
                                    />
                                </Grid>
                            )}

                            {isInstall && purposeApplies && (
                                <Grid item xs={12} md={6}>
                                    <Select
                                        required
                                        options={dropdownOptions.transferReasons}
                                        label={TranslateW('PurposeForAddingGas')}
                                        value={serviceTransferReasonId}
                                        onChange={e => handlePurposeForAddingGas(e.target.value)}
                                        disabled={isRecordRefrigerantSE}
                                        error={requiredFieldsMissing.serviceTransferReasonId}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Input
                                    fullWidth
                                    label={TranslateW('installGas.cylinder-notes')}
                                    value={entryForm.cylinderNotes}
                                    onChange={e => setEntryForm(prev => ({ ...prev, cylinderNotes: e.target.value }))}
                                    maxCharacterLength={50}
                                    disabled={entryForm.selectedCylinder.id === null}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Input
                                    fullWidth
                                    label={TranslateW('installGas.tranfer-notes')}
                                    value={entryForm.transferNotes}
                                    onChange={e => setEntryForm(prev => ({ ...prev, transferNotes: e.target.value }))}
                                    maxCharacterLength={50}
                                    disabled={entryForm.selectedCylinder.id === null}
                                />
                            </Grid>

                            {/* "Was appropriate vacuum pulled?" lives in the recover form for all SE types
                                (per Figma "New Transfer" container + AC3). Shutdown/Mothball is the one
                                exception: it renders the question at the step level (above) so a recover-only
                                event stays completable without opening the form. Both write the same
                                formState.wasProperVacuumPulled / vacuumPsi. */}
                            {isRecover && !isShutdownMothballSE && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <SegmentedButtonGroup
                                            label={TranslateW('recoverGas.was-appropriate-vacuum-pulled')}
                                            value={formState.wasProperVacuumPulled}
                                            onChange={value => setGlobalState('wasProperVacuumPulled', value)}
                                            options={(yes_no_options || []).map(o => ({ value: o.id, label: o.name }))}
                                        />
                                    </Grid>
                                    {formState.wasProperVacuumPulled === true && (
                                        <Grid item xs={12} md={6}>
                                            <Input
                                                fullWidth
                                                type="number"
                                                label={TranslateW('VacuumInHG')}
                                                value={formState.vacuumPsi}
                                                onChange={e => setGlobalState('vacuumPsi', e.target.value)}
                                            />
                                        </Grid>
                                    )}
                                </>
                            )}

                            {isRecover && recoverAssetOverflow && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        {Translate('RecoveryAmountLimit', {
                                            weight: renderWeight(assetMaxGasCapacity),
                                        })}
                                    </Alert>
                                </Grid>
                            )}
                            {isRecover && recoverCylinderOverflow && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        {TranslateW('AmountTransferLimit')}
                                    </Alert>
                                </Grid>
                            )}
                            {isInstall && (installCylinderOverflow || installAssetOverflow) && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        {Translate('InstallAmountLimit', {
                                            weight: renderWeight(
                                                installAssetOverflow ? assetMaxGasCapacity
                                                    : installNewAssetOverflow ? newGasChargeCapacityLbs
                                                        : entryForm.selectedCylinder.currentGasWeightLbs
                                            ),
                                        })}
                                    </Alert>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <SecondaryButton
                                        buttonText="Cancel"
                                        onClick={handleCancel}
                                    />
                                    <SecondaryButton
                                        buttonText={isEditing ? 'Save Changes' : 'Save Transfer'}
                                        onClick={handleSave}
                                        disabled={disableSave}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}

            {showStepLevelInstallWarning && (
                <Grid item xs={12}>
                    <Alert severity="error">{stepLevelInstallWarningText}</Alert>
                </Grid>
            )}

            {showStepLevelRecoverWarning && (
                <Grid item xs={12}>
                    <Alert severity="error">
                        {Translate('RecoveryAmountLimit', {
                            weight: renderWeight(assetMaxGasCapacity),
                        })}
                    </Alert>
                </Grid>
            )}

            {/* AC5 — the transfers table is hidden until the first transfer is saved
                (subject to SE-type visibility). */}
            {showRecoveryTable && recoveryRows.length > 0 && (
                <TransferTableSection
                    title="RECOVERY TRANSFERS"
                    count={recoveryRows.length}
                    columns={recoveryColumns}
                    rows={recoveryRows}
                    emptyText="No recovery transfers added yet"
                    mode="recover"
                    total={runningRecoverTotal}
                    totalLabel="Subtotal Recovered"
                    onEdit={handleEdit(TRANSFER_TYPE.RECOVER)}
                    onDelete={handleDeleteRequest(TRANSFER_TYPE.RECOVER)}
                />
            )}

            {showInstallTable && installRows.length > 0 && (
                <TransferTableSection
                    title="INSTALL TRANSFERS"
                    count={installRows.length}
                    columns={installColumns}
                    rows={installRows}
                    emptyText="No install transfers added yet"
                    mode="install"
                    total={runningInstallTotal}
                    totalLabel="Subtotal Installed"
                    onEdit={handleEdit(TRANSFER_TYPE.INSTALL)}
                    onDelete={handleDeleteRequest(TRANSFER_TYPE.INSTALL)}
                />
            )}

            {(recoveryRows.length > 0 || installRows.length > 0) && (
                <NetSummaryStrip
                    totalRecovered={runningRecoverTotal}
                    totalInstalled={runningInstallTotal}
                    showRecovered={showRecoveryTable}
                    showInstalled={showInstallTable}
                />
            )}

            <DeleteModal
                open={!!pendingDelete}
                title="Delete Transfer"
                description={
                    pendingDelete
                        ? `Delete this ${deleteModeLabel} transfer for ${pendingDelete.name || 'this cylinder'}?`
                        : ''
                }
                onConfirm={confirmDelete}
                onClose={() => setPendingDelete(null)}
                showImage={false}
            />
        </Grid>
    );
};

export default GasTransaction;
